<template>
  <div>
    <b-card>
      <TitleTable titleTable="Items" iconTable="fas fa-boxes" />
      <b-row class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="3">
          <b-form-group label="Category" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.inv_category_id"
              :options="categoryOptions"
              :normalizer="categoryNormalizer"
              :clearable="true"
              placeholder="All categories"
              :disabled="optionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Distributor" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.inv_distributor_id"
              :options="distributorOptions"
              :normalizer="distributorNormalizer"
              :clearable="true"
              placeholder="All distributors"
              :disabled="optionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="Status" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-select v-model="filters.active" :options="activeFilterOptions">
              <template #first>
                <b-form-select-option :value="null">All</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="Search" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input
              v-model="filters.search"
              placeholder="Name or SKU..."
              @keyup.enter="load(1)" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="facilityNormalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="optionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="1" class="d-flex justify-content-end align-items-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn mt-2 mt-md-0"
            :disabled="optionsLoading || loading"
            @click="load(1)">
            Filter
          </b-button>
        </b-col>
      </b-row>
      <b-row class="m-1 mb-3">
        <b-col cols="12" class="d-flex justify-content-end align-items-center pt-2 border-top">
          <GoBack class="mr-2" />
          <b-button variant="primary" @click="openAdd">Add item</b-button>
        </b-col>
      </b-row>

      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No items found">
        <template #cell(category)="data">
          {{ categoryLabel(data.item) }}
        </template>
        <template #cell(reorder_point)="data">
          <span :class="{ 'text-muted font-italic': isNullish(data.value) }">
            {{ displayValue(data.value) }}
          </span>
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEdit(data.item)">
            <i class="fas fa-pen"></i>
          </b-button>
        </template>
      </b-table>

      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalUsers"
        :perPage="perPage"
        :currentPage="{ page: currentPage }"
        @page-changed="load" />
    </b-card>

    <WlFormSidebar
      :visible.sync="sidebarVisible"
      sidebar-id="inv-item-sidebar"
      :title="editId ? 'Edit item' : 'New item'"
      :loading="saving"
      @submit="save">
      <b-form-group label="Category">
        <treeselect
          v-model="form.inv_category_id"
          :options="categoryOptions"
          :normalizer="categoryNormalizer"
          placeholder="Select category" />
      </b-form-group>
      <b-form-group label="Name">
        <b-form-input v-model="form.name" />
      </b-form-group>
      <b-form-group label="Unit">
        <b-form-input v-model="form.unit" placeholder="vial, ml..." />
      </b-form-group>
      <b-form-group label="SKU">
        <b-form-input v-model="form.sku" />
      </b-form-group>
      <b-form-group label="Reorder point">
        <b-form-input v-model="form.reorder_point" type="number" />
      </b-form-group>
      <b-form-group label="Distributor">
        <treeselect
          v-model="form.inv_distributor_id"
          :options="distributorOptions"
          :normalizer="distributorNormalizer"
          :clearable="true"
          placeholder="Optional" />
      </b-form-group>
    </WlFormSidebar>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import { BCard, BRow, BCol, BTable, BButton, BFormGroup, BFormInput, BFormSelect } from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import PaginationTable from '@/components/PaginationTable'
import ToastNotification from '@/components/ToastNotification'
import WlFormSidebar from '../components/WlFormSidebar.vue'
import invItemsApi from '@/core/services/api/weightloss-inventory/items'
import invCategoriesApi from '@/core/services/api/weightloss-inventory/categories'
import invDistributorsApi from '@/core/services/api/weightloss-inventory/distributors'
import wlAdminMixin from '../mixins/wlAdminMixin'
import axiosF from '@/core/services/api/admin/facilities'

export default {
  name: 'TableInvItems',
  mixins: [wlAdminMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    Treeselect,
    TitleTable,
    GoBack,
    PaginationTable,
    ToastNotification,
    WlFormSidebar,
  },
  data() {
    return {
      optionsLoading: false,
      filters: {
        inv_category_id: null,
        inv_distributor_id: null,
        facilities_id: [],
        active: true,
        search: '',
      },
      activeFilterOptions: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' },
      ],
      categoryOptions: [],
      distributorOptions: [],
      facilityOptions: [],
      categoryNormalizer(node) {
        return { id: node.id, label: node.label || node.name }
      },
      distributorNormalizer(node) {
        return { id: node.id, label: node.label || node.name }
      },
      facilityNormalizer(node) {
        return { id: node.id, label: node.label || node.name }
      },
      fields: [
        { key: 'id', label: 'ID', thStyle: { width: '4%' } },
        { key: 'name', label: 'Name' },
        { key: 'sku', label: 'SKU' },
        { key: 'unit', label: 'Unit' },
        { key: 'category', label: 'Category' },
        { key: 'reorder_point', label: 'Reorder' },
        { key: 'actions', label: '' },
      ],
      sidebarVisible: false,
      saving: false,
      editId: null,
      form: {
        inv_category_id: null,
        name: '',
        unit: '',
        sku: '',
        reorder_point: null,
        inv_distributor_id: null,
      },
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.optionsLoading = true
      try {
        const [cats, dists, facilitiesRes] = await Promise.all([
          invCategoriesApi.list(),
          invDistributorsApi.list(),
          axiosF.facilityList(1000),
        ])
        this.categoryOptions = cats.map(c => ({ id: c.id, label: c.name }))
        this.distributorOptions = dists.map(d => ({ id: d.id, label: d.name }))
        this.facilityOptions = (facilitiesRes.data || []).map(f => ({
          id: f.id,
          label: f.name,
        }))
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.optionsLoading = false
      }
      this.load(1)
    },
    buildListParams() {
      return this.buildListQueryParams({
        inv_category_id: this.filters.inv_category_id,
        inv_distributor_id: this.filters.inv_distributor_id,
        facilities_id: this.filters.facilities_id,
        active: this.filters.active,
        search: this.filters.search?.trim() || null,
      })
    },
    isNullish(value) {
      return value === null || value === undefined || value === ''
    },
    displayValue(value) {
      return this.isNullish(value) ? '—' : value
    },
    categoryLabel(item) {
      if (item && item.category && item.category.name) return item.category.name
      const id = item && (item.inv_category_id || (item.category && item.category.id))
      if (id != null) {
        const c = this.categoryOptions.find(x => x.id === id || x.id === Number(id))
        return c ? c.label : `#${id}`
      }
      return '—'
    },
    async load(page = 1) {
      this.loading = true
      try {
        const res = await invItemsApi.listPaginated(
          this.perPage,
          this.buildListParams(),
          page
        )
        this.applyPagination(res)
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.loading = false
      }
    },
    openAdd() {
      this.editId = null
      this.form = {
        inv_category_id: null,
        name: '',
        unit: '',
        sku: '',
        reorder_point: null,
        inv_distributor_id: null,
      }
      this.sidebarVisible = true
    },
    openEdit(item) {
      this.editId = item.id
      this.form = {
        inv_category_id: item.inv_category_id,
        name: item.name || '',
        unit: item.unit || '',
        sku: item.sku || '',
        reorder_point: item.reorder_point,
        inv_distributor_id: item.inv_distributor_id,
      }
      this.sidebarVisible = true
    },
    async save() {
      if (!this.form.inv_category_id || !String(this.form.name || '').trim()) {
        this.$refs.toast.danger('Category and name are required')
        return
      }
      this.saving = true
      try {
        const payload = {
          inv_category_id: Number(this.form.inv_category_id),
          name: String(this.form.name).trim(),
          unit: String(this.form.unit || '').trim(),
          sku: String(this.form.sku || '').trim(),
          reorder_point: this.form.reorder_point != null && this.form.reorder_point !== ''
            ? Number(this.form.reorder_point)
            : null,
        }
        if (this.form.inv_distributor_id) {
          payload.inv_distributor_id = Number(this.form.inv_distributor_id)
        }
        if (this.editId) {
          await invItemsApi.update(this.editId, payload)
          this.$refs.toast.success('Item updated')
        } else {
          await invItemsApi.create(payload)
          this.$refs.toast.success('Item created')
        }
        this.sidebarVisible = false
        this.load(this.currentPage)
      } catch (e) {
        this.handleApiError(e, this.$refs.toast)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}
</style>
